import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, input } from '@angular/core'
import { IconComponent } from '../icon'

/**
 * A presentational component that applies link styling to an "a" or "button" element.
 *
 * To open in a new tab, add a native `target="_blank"`. Using the native attribute means
 * it works identically for plain hrefs and for `RouterLink`.
 *
 * @example
 * ``` html
 * <!-- with RouterLink -->
 * <a ftrLink [routerLink]="['../']" [queryParams]="{ q: '123' }">
 *   Open a route
 * </a>
 * <!-- with href -->
 * <a ftrLink href="https://app.fortherecord.com" target="_blank">
 *   Open a link in a new tab
 * </a>
 * <!-- as button -->
 * <button ftrLink (click)="someHandler()">
 *   This button looks like a link
 * </button>
 * ```
 */
@Component({
  selector: 'a[ftrLink], button[ftrLink]',
  templateUrl: './link.component.html',
  styleUrl: './link.component.css',
  imports: [IconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': 'classes()',
    '[attr.rel]': 'shouldOpenInNewTab ? "noopener noreferrer" : null',
  },
})
export class LinkComponent {
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef)

  readonly linkIsBold = input<boolean>(false)
  readonly linkIsGrey = input<boolean>(false)
  readonly linkNoUnderline = input<boolean>(false)

  protected readonly shouldOpenInNewTab =
    this.element.nativeElement instanceof HTMLAnchorElement &&
    this.element.nativeElement.getAttribute('target') === '_blank'

  protected readonly classes = computed(() => ({
    'link-display': true,
    'link-display--bold': this.linkIsBold(),
    'link-display--grey': this.linkIsGrey(),
    'link-display--no-underline': this.linkNoUnderline(),
  }))
}
